import { useEffect, useRef, useState, ReactNode } from 'react'

/**
 * Обёртка для появления элемента при скролле в viewport.
 * Применяет `animate-fade-up` (из tailwind.config) и `delay-N` если задан.
 * Один раз сработавшая анимация не повторяется (`once: true` по дефолту).
 */
export function Reveal({
  children,
  delay = 0,
  className = '',
}: {
  children: ReactNode
  delay?: number
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Если IO нет (старые браузеры) — сразу показать
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          io.disconnect()
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' },
    )
    io.observe(el)

    // Fallback — если за 3 сек элемент так и не intersect (например, ниже viewport,
    // пользователь не доскроллил, но дёрнул resize / открыл deeplink в середине страницы) —
    // показываем без анимации, чтобы не оставлять пустые места.
    const timer = setTimeout(() => setVisible(true), 3000)

    return () => {
      io.disconnect()
      clearTimeout(timer)
    }
  }, [])

  return (
    <div
      ref={ref}
      style={{ animationDelay: visible ? `${delay}ms` : undefined }}
      className={`${visible ? 'animate-fade-up' : 'opacity-0'} ${className}`}
    >
      {children}
    </div>
  )
}
